import {
  Check as CheckIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  PlayArrow as RunningIcon,
  QueryStats as StatsIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Storage as ETLIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControlLabel,
  GridLegacy as Grid,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import moment from "moment-timezone";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminEmptyState,
  AdminFilterPanel,
  PageShell,
  AdminTableActions,
  AdminTableFrame,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { Job, JobService } from "@/services/features/JobService";
import {
  formatRelativeStoreTime,
  formatStoreDateTimeWithTimezone,
  getBrowserTimezone,
} from "@/utils/storeTime";

interface CustomerOption {
  id: number;
  label: string;
}

interface UpdateSearchParamsType {
  customerId?: string | null;
  jobName?: string | null;
  status?: string | null;
  craveOnly?: string | null;
}

const etlStatusKeyMap: Record<string, string> = {
  SUCCESSFUL: "SUCCESSFUL",
  FAILED: "FAILED",
  RUNNING: "RUNNING",
  IN_PROGRESS: "inProgress",
  "IN PROGRESS": "inProgress",
  INPROGRESS: "inProgress",
  JOB_STARTED: "jobStarted",
  MACHINE_CREATED: "machineCreated",
  JOB_COMPLETED: "jobCompleted",
  JOB_FAILED: "jobFailed",
  MACHINE_DELETED: "machineDeleted",
  MACHINE_TIMEOUT: "machineTimeout",
};

const normalizeStatus = (status: string) => status.replace(/\s+/g, "_").toUpperCase();

const isInProgressStatus = (status: string) => {
  const normalizedStatus = normalizeStatus(status);
  return ["RUNNING", "IN_PROGRESS", "INPROGRESS"].includes(normalizedStatus);
};

const JOB_STATUSES = ["FAILED", "SUCCESSFUL", "IN_PROGRESS"];
type EtlStatusColor = "default" | "error" | "primary" | "success";
type EtlSortColumn = "jobName" | "customer" | "createdAt" | "completedAt" | "duration" | "status";

const ETLRuns: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobNames, setJobNames] = useState<string[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showOnlyCraveJobs, setShowOnlyCraveJobs] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const jobService = JobService.getInstance();
  const customerService = CustomerService.getInstance();
  const browserTimezone = getBrowserTimezone();
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    document.title = t("operations.etl.eTLRuns.documentTitle");
    void fetchInitialData();
  }, [t]);

  useEffect(() => {
    const customerId = searchParams.get("customerId");
    const jobName = searchParams.get("jobName");
    const status = searchParams.get("status");

    if (customerId === "0") {
      setSelectedCustomer({ id: 0, label: "Global" });
    } else if (customerId) {
      const customer = customers.find((c) => c.id === Number(customerId));
      if (customer) {
        setSelectedCustomer({ id: customer.id, label: customer.name });
      }
    }

    if (jobName) {
      setSelectedJob(jobName);
    }

    if (status) {
      setSelectedStatus(status);
    }

    void fetchJobs(
      customerId ? Number(customerId) : undefined,
      jobName || undefined,
      status || undefined,
    );
  }, [searchParams, customers]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const customerResponse = await customerService.getAllCustomers();
      if (customerResponse.success && customerResponse.data) {
        setCustomers(customerResponse.data);
      } else {
        enqueueSnackbar(t("operations.etl.eTLRuns.notifications.failedFetchCustomers"), {
          variant: "error",
        });
      }
    } catch {
      enqueueSnackbar(t("operations.etl.eTLRuns.notifications.failedFetchInitialData"), {
        variant: "error",
      });
    }

    try {
      const jobNamesResponse = await jobService.getJobNames();
      if (jobNamesResponse.success && jobNamesResponse.data) {
        setJobNames(jobNamesResponse.data);
      } else {
        enqueueSnackbar(t("operations.etl.eTLRuns.notifications.failedFetchJobs"), {
          variant: "error",
        });
      }
    } catch {
      enqueueSnackbar(t("operations.etl.eTLRuns.notifications.failedFetchJobs"), {
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const fetchJobs = async (customerId?: number, jobName?: string, status?: string) => {
    setIsLoading(true);
    try {
      const response = await jobService.getJobList(customerId, jobName, status);
      if (response.success && response.data) {
        setJobs(response.data);
      } else {
        enqueueSnackbar(t("operations.etl.eTLRuns.notifications.failedFetchJobs"), {
          variant: "error",
        });
      }
    } catch {
      enqueueSnackbar(t("operations.etl.eTLRuns.notifications.failedFetchJobs"), {
        variant: "error",
      });
    }
    setIsLoading(false);
  };

  const updateSearchParams = (params: UpdateSearchParamsType) => {
    const newParams = new URLSearchParams();
    if (params.customerId) newParams.set("customerId", params.customerId);
    if (params.jobName) newParams.set("jobName", params.jobName);
    if (params.status) newParams.set("status", params.status);
    if (params.craveOnly) newParams.set("craveOnly", params.craveOnly);
    setSearchParams(newParams);
  };

  const handleCustomerChange = (value: CustomerOption | null) => {
    setSelectedCustomer(value);
    updateSearchParams({
      customerId: value?.id.toString(),
      jobName: selectedJob,
      status: selectedStatus,
      craveOnly: showOnlyCraveJobs ? "true" : null,
    });
  };

  const handleJobChange = (value: string | null) => {
    setSelectedJob(value);
    updateSearchParams({
      customerId: selectedCustomer?.id.toString(),
      jobName: value,
      status: selectedStatus,
      craveOnly: showOnlyCraveJobs ? "true" : null,
    });
  };

  const handleStatusChange = (value: string | null) => {
    setSelectedStatus(value);
    updateSearchParams({
      customerId: selectedCustomer?.id.toString(),
      jobName: selectedJob,
      status: value,
      craveOnly: showOnlyCraveJobs ? "true" : null,
    });
  };

  const handleCraveJobsToggle = (checked: boolean) => {
    setShowOnlyCraveJobs(checked);
    if (checked) {
      setSelectedCustomer(null);
    }
    updateSearchParams({
      customerId: checked ? undefined : selectedCustomer?.id?.toString(),
      jobName: selectedJob,
      status: selectedStatus,
      craveOnly: checked ? "true" : null,
    });
  };

  const handleReset = () => {
    setSelectedCustomer(null);
    setSelectedJob(null);
    setSelectedStatus(null);
    setShowOnlyCraveJobs(false);
    setSearchParams(new URLSearchParams());
  };

  const getStatusColor = (status: string): EtlStatusColor => {
    switch (normalizeStatus(status)) {
      case "SUCCESSFUL":
      case "COMPLETED":
      case "JOB_COMPLETED":
        return "success";
      case "FAILED":
      case "ERROR":
      case "JOB_FAILED":
        return "error";
      case "RUNNING":
      case "IN_PROGRESS":
      case "INPROGRESS":
        return "primary";
      default:
        return "default";
    }
  };

  const getStatusChipSx = (status: string, minWidth: number) => (theme: Theme) => {
    const color = getStatusColor(status);

    if (color === "default") {
      return { minWidth };
    }

    const paletteColor = theme.palette[color];
    return {
      minWidth,
      backgroundColor: alpha(paletteColor.main, 0.1),
      border: "1px solid",
      borderColor: alpha(paletteColor.main, 0.3),
      color: paletteColor.main,
      "& .MuiChip-icon": { color: paletteColor.main },
    };
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    switch (normalizeStatus(status)) {
      case "SUCCESSFUL":
      case "COMPLETED":
      case "JOB_COMPLETED":
        return <CheckIcon />;
      case "FAILED":
      case "ERROR":
      case "JOB_FAILED":
        return <CloseIcon />;
      case "RUNNING":
      case "IN_PROGRESS":
      case "INPROGRESS":
        return <ScheduleIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const customerOptions: CustomerOption[] = [
    { id: 0, label: t("operations.etl.eTLRuns.options.global") },
    ...customers.map((customer) => ({
      id: customer.id,
      label: customer.name,
    })),
  ];

  const jobOptions = jobNames;

  const getStatusLabel = (status: string) => {
    const normalizedStatus = normalizeStatus(status);

    if (["SUCCESSFUL", "COMPLETED", "JOB_COMPLETED"].includes(normalizedStatus)) {
      return t("operations.etl.eTLRuns.status.completed");
    }

    if (["FAILED", "ERROR", "JOB_FAILED"].includes(normalizedStatus)) {
      return t("operations.etl.eTLRuns.status.error");
    }

    if (isInProgressStatus(normalizedStatus)) {
      return t("operations.etl.eTLRuns.status.inProgress");
    }

    return t(
      `operations.etl.common.status.${etlStatusKeyMap[normalizedStatus] ?? normalizedStatus}`,
    );
  };

  const getDurationText = (duration: moment.Duration | null, status: string) => {
    if (duration) {
      if (duration.asMinutes() < 1) {
        return t("operations.etl.eTLRuns.duration.seconds", {
          seconds: Math.round(duration.asSeconds()),
        });
      }

      if (duration.hours() > 0) {
        return t("operations.etl.eTLRuns.duration.hoursMinutes", {
          hours: duration.hours(),
          minutes: duration.minutes(),
        });
      }

      return t("operations.etl.eTLRuns.duration.minutesSeconds", {
        minutes: duration.minutes(),
        seconds: duration.seconds(),
      });
    }

    return isInProgressStatus(status)
      ? t("operations.etl.eTLRuns.duration.running")
      : t("operations.etl.eTLRuns.duration.notAvailable");
  };

  // Filter jobs based on all criteria including Crave Jobs toggle
  const filteredJobs = jobs.filter((job) => {
    if (showOnlyCraveJobs) {
      return !customers.some((c) => c.id === job.customerId);
    }
    return true;
  });

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    Job,
    EtlSortColumn
  >(
    filteredJobs,
    (job, column) => {
      const createdAt = new Date(job.createdAt).getTime();
      const completedAt = job.completedAt ? new Date(job.completedAt).getTime() : null;
      const duration =
        Number.isFinite(createdAt) && completedAt !== null && Number.isFinite(completedAt)
          ? completedAt - createdAt
          : null;

      switch (column) {
        case "jobName":
          return job.name;
        case "customer":
          return (
            customers.find((customer) => customer.id === job.customerId)?.name ??
            t("operations.etl.eTLRuns.text.craveJob")
          );
        case "createdAt":
          return Number.isFinite(createdAt) ? createdAt : null;
        case "completedAt":
          return completedAt !== null && Number.isFinite(completedAt) ? completedAt : null;
        case "duration":
          return duration;
        case "status":
          return getStatusLabel(job.status);
      }
    },
    "createdAt",
    locale,
    "desc",
  );

  // Get job statistics
  const getJobStats = () => {
    const stats = {
      total: jobs.length,
      successful: jobs.filter((job) => job.status === "SUCCESSFUL").length,
      failed: jobs.filter((job) => job.status === "FAILED").length,
      inProgress: jobs.filter((job) => isInProgressStatus(job.status)).length,
      craveJobs: jobs.filter((job) => !customers.some((c) => c.id === job.customerId)).length,
    };
    return stats;
  };

  const emptyStateText =
    !selectedCustomer && !showOnlyCraveJobs
      ? t("operations.etl.eTLRuns.empty.selectCustomerOrEnableCraveJobs")
      : t("operations.etl.eTLRuns.empty.noEtlRunsAvailable");

  const renderMobileView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {filteredJobs.length > 0 ? (
        filteredJobs.map((job) => {
          const customer = customers.find((c) => c.id === job.customerId);
          const startedAt = moment(job.createdAt);
          const completedAt = job.completedAt ? moment(job.completedAt) : null;
          const duration =
            startedAt && completedAt ? moment.duration(completedAt.diff(startedAt)) : null;

          return (
            <Card key={job.id} sx={{ p: 1.5 }}>
              {/* Header with Job Name, Customer, and Status */}
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      mb: 0.5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {job.name}
                  </Typography>
                  {customer ? (
                    <Typography variant="caption" color="text.secondary">
                      {customer.name}
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box
                        component="img"
                        src="/images/Crave Retail mini.png"
                        alt={t("operations.etl.eTLRuns.accessibility.craveLogo")}
                        sx={{ width: 12, height: 12, objectFit: "contain" }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {t("operations.etl.eTLRuns.text.craveJob")}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Chip
                  icon={getStatusIcon(job.status)}
                  label={getStatusLabel(job.status)}
                  color={getStatusColor(job.status)}
                  size="small"
                  sx={getStatusChipSx(job.status, 90)}
                />
              </Stack>

              <Box sx={{ mt: 1.5, display: "flex", gap: 2, flexWrap: "wrap" }}>
                {/* Timing Information */}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("operations.etl.eTLRuns.fields.created")}
                  </Typography>
                  <Typography variant="body2">
                    {formatStoreDateTimeWithTimezone(job.createdAt, browserTimezone, locale)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {job.completedAt
                      ? t("operations.etl.eTLRuns.fields.completed")
                      : t("operations.etl.eTLRuns.fields.status")}
                  </Typography>
                  {job.completedAt ? (
                    <Typography variant="body2">
                      {formatStoreDateTimeWithTimezone(job.completedAt, browserTimezone, locale)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="info.main">
                      {t("operations.etl.common.status.inProgress")}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("operations.etl.eTLRuns.fields.duration")}
                  </Typography>
                  <Typography variant="body2">{getDurationText(duration, job.status)}</Typography>
                </Box>

                {/* View Details Button */}
                <Box sx={{ ml: "auto", alignSelf: "flex-end" }}>
                  <Button
                    startIcon={<ViewIcon />}
                    size="small"
                    onClick={() => {
                      if (!job.id) {
                        enqueueSnackbar(t("operations.etl.eTLRuns.notifications.invalidJobId"), {
                          variant: "error",
                        });
                        return;
                      }
                      void navigate(`/admin/data/etl-runs/${job.id}`);
                    }}
                  >
                    {t("operations.etl.eTLRuns.actions.details")}
                  </Button>
                </Box>
              </Box>
            </Card>
          );
        })
      ) : (
        <Card>
          <Box sx={{ p: 2 }}>
            <AdminEmptyState icon={<ETLIcon sx={{ fontSize: 40 }} />} text={emptyStateText} />
          </Box>
        </Card>
      )}
    </Box>
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("operations.etl.eTLRuns.title")}
      icon={<ETLIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            void fetchJobs(
              selectedCustomer?.id,
              selectedJob || undefined,
              selectedStatus || undefined,
            );
          }}
        >
          {t("operations.etl.eTLRuns.actions.refresh")}
        </Button>
      }
    >
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <StatsIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("operations.etl.eTLRuns.text.totalJobs")}
                </Typography>
                <Typography variant="h4">{getJobStats().total}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckIcon color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("operations.etl.common.status.SUCCESSFUL")}
                </Typography>
                <Typography variant="h4">{getJobStats().successful}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <ErrorIcon color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("operations.etl.common.status.FAILED")}
                </Typography>
                <Typography variant="h4">{getJobStats().failed}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <RunningIcon color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("operations.etl.common.status.inProgress")}
                </Typography>
                <Typography variant="h4">{getJobStats().inProgress}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                component="img"
                src="/images/Crave Retail mini.png"
                alt={t("operations.etl.eTLRuns.accessibility.craveLogo")}
                sx={{ width: 40, height: 40, objectFit: "contain" }}
              />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("operations.etl.eTLRuns.text.craveJobs")}
                </Typography>
                <Typography variant="h4">{getJobStats().craveJobs}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Card */}
      <AdminFilterPanel sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <MuiAutocompleteMultiSelect
              label={t("operations.etl.eTLRuns.fields.selectCustomer")}
              options={customerOptions}
              value={selectedCustomer ? [selectedCustomer] : []}
              onChange={(next) => handleCustomerChange(next[0] ?? null)}
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.id}
              disabled={showOnlyCraveJobs || (isLoading && customers.length === 0)}
              loading={isLoading && customers.length === 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MuiAutocompleteMultiSelect
              label={t("operations.etl.eTLRuns.fields.selectJob")}
              options={jobOptions}
              value={selectedJob ? [selectedJob] : []}
              onChange={(next) => handleJobChange(next[0] ?? null)}
              getOptionLabel={(option) => option}
              getOptionKey={(option) => option}
              disabled={isLoading && jobNames.length === 0}
              loading={isLoading && jobNames.length === 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MuiAutocompleteMultiSelect
              label={t("operations.etl.eTLRuns.fields.selectStatus")}
              options={JOB_STATUSES}
              value={selectedStatus ? [selectedStatus] : []}
              onChange={(next) => handleStatusChange(next[0] ?? null)}
              getOptionLabel={(option) => getStatusLabel(option)}
              getOptionKey={(option) => option}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyCraveJobs}
                onChange={(e) => handleCraveJobsToggle(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="img"
                  src="/images/Crave Retail mini.png"
                  alt={t("operations.etl.eTLRuns.accessibility.craveLogo")}
                  sx={{ width: 20, height: 20, objectFit: "contain" }}
                />
                <Typography>{t("operations.etl.eTLRuns.text.showOnlyCraveJobs")}</Typography>
              </Stack>
            }
          />

          {(selectedCustomer || selectedJob || selectedStatus || showOnlyCraveJobs) && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              color="primary"
              size="medium"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {t("common.clearFilters")}
            </Button>
          )}
        </Box>
      </AdminFilterPanel>

      {/* Jobs Table/Cards */}
      <Box>
        {isMobile ? (
          renderMobileView()
        ) : (
          <AdminTableFrame>
            <TableHead>
              <TableRow>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="jobName"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("operations.etl.eTLRuns.fields.jobName")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="customer"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("common.customer")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="createdAt"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("operations.etl.eTLRuns.fields.createdAt")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="completedAt"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("operations.etl.eTLRuns.fields.completedAt")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="duration"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("operations.etl.eTLRuns.fields.duration")}
                </SortableTableCell>
                <SortableTableCell
                  align="center"
                  activeColumn={sortColumn}
                  column="status"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("operations.etl.eTLRuns.fields.status")}
                </SortableTableCell>
                <TableCell align="right">{t("operations.etl.eTLRuns.fields.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.length > 0 ? (
                sortedItems.map((job) => {
                  const customer = customers.find((c) => c.id === job.customerId);
                  const startedAt = moment(job.createdAt);
                  const completedAt = job.completedAt ? moment(job.completedAt) : null;
                  const duration =
                    startedAt && completedAt ? moment.duration(completedAt.diff(startedAt)) : null;

                  return (
                    <TableRow
                      key={job.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <TableCell>{job.name}</TableCell>
                      <TableCell>
                        {customer ? (
                          customer.name
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              component="img"
                              src="/images/Crave Retail mini.png"
                              alt={t("operations.etl.eTLRuns.accessibility.craveLogo")}
                              sx={{ width: 20, height: 20, objectFit: "contain" }}
                            />
                            <Typography>{t("operations.etl.eTLRuns.text.craveJob")}</Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        {startedAt ? (
                          <Stack direction="column" spacing={0.5}>
                            <Typography variant="body2">
                              {formatStoreDateTimeWithTimezone(
                                job.createdAt,
                                browserTimezone,
                                locale,
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatRelativeStoreTime(job.createdAt, browserTimezone)}
                            </Typography>
                          </Stack>
                        ) : (
                          t("operations.etl.eTLRuns.duration.notAvailable")
                        )}
                      </TableCell>
                      <TableCell>
                        {job.completedAt ? (
                          <Stack direction="column" spacing={0.5}>
                            <Typography variant="body2">
                              {formatStoreDateTimeWithTimezone(
                                job.completedAt,
                                browserTimezone,
                                locale,
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatRelativeStoreTime(job.completedAt, browserTimezone)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="info.main">
                            {t("operations.etl.common.status.inProgress")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {duration ? (
                          <Tooltip
                            title={t("operations.etl.eTLRuns.duration.tooltip", {
                              hours: duration.hours(),
                              minutes: duration.minutes(),
                              seconds: duration.seconds(),
                            })}
                          >
                            <Typography variant="body2">
                              {getDurationText(duration, job.status)}
                            </Typography>
                          </Tooltip>
                        ) : isInProgressStatus(job.status) ? (
                          <Typography variant="body2" color="info.main">
                            {t("operations.etl.eTLRuns.duration.running")}
                          </Typography>
                        ) : (
                          t("operations.etl.eTLRuns.duration.notAvailable")
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={getStatusLabel(job.status)}
                          color={getStatusColor(job.status)}
                          size="small"
                          sx={getStatusChipSx(job.status, 100)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <AdminTableActions
                          actions={[
                            {
                              icon: <ViewIcon />,
                              hint: t("operations.etl.eTLRuns.tooltips.viewDetails"),
                              action: () => {
                                if (!job.id) {
                                  enqueueSnackbar(
                                    t("operations.etl.eTLRuns.notifications.invalidJobId"),
                                    { variant: "error" },
                                  );
                                  return;
                                }
                                void navigate(`/admin/data/etl-runs/${job.id}`);
                              },
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <AdminEmptyState
                      icon={<ETLIcon sx={{ fontSize: 40 }} />}
                      text={emptyStateText}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </AdminTableFrame>
        )}
      </Box>
    </PageShell>
  );
};

export default ETLRuns;
